import { useState } from 'react'
import {
  BOOKING_STATUS_LABELS,
  OWNER_TIMEZONE,
  SLOT_LENGTHS,
  SLOT_LENGTH_LABELS,
  type AvailabilityWindow,
  type Booking,
  type SlotLength,
} from '@diana/shared'
import type { CalendarWeek } from '../../lib/api.js'
import { hhmm, timeIn } from '../../lib/datetime.js'
import { Button, ErrorLine, FieldLabel, Rows, Status } from '../../classroom/ui.js'
import { Choice, DANGER_LINK, Sheet, SheetSection } from './calendarUi.js'
import { PlanClassLine } from './PlanSection.js'

type Props = {
  date: string
  windows: AvailabilityWindow[]
  bookings: Booking[]
  /** The plan lessons tied to these bookings. */
  planItems?: CalendarWeek['planItems']
  onClose: () => void
  onAdd: (startTime: string, endTime: string, slotMinutes: SlotLength) => void
  onDelete: (id: string) => void
  busy: boolean
  error: string
}

const PRESETS: { label: string; start: string; end: string }[] = [
  { label: 'Rano', start: '09:00', end: '12:00' },
  { label: 'Popołudnie', start: '14:00', end: '17:00' },
  { label: 'Wieczór', start: '17:00', end: '20:00' },
]

/**
 * What opens when Diana clicks a day in the month: who has booked, the
 * windows she's opened, and a form to open more time.
 */
export function DayEditor({ date, windows, bookings, planItems = [], onClose, onAdd, onDelete, busy, error }: Props) {
  const [start, setStart] = useState('16:00')
  const [end, setEnd] = useState('20:00')
  const [slot, setSlot] = useState<SlotLength>(30)

  const pretty = new Intl.DateTimeFormat('pl-PL', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: OWNER_TIMEZONE,
  }).format(new Date(`${date}T12:00:00Z`))

  const valid = end > start

  return (
    <Sheet label={`Dzień ${pretty}`} title={pretty} onClose={onClose}>
      {/* Who has booked. Shown first — it's the thing she opens this for. */}
      <SheetSection title="Zapisani">
        <p className="m-0 -mt-1 text-class-label text-class-ink-faint">Potwierdzanie próśb i wpisywanie uczniów jest w widoku tygodnia.</p>
        {bookings.length === 0 ? (
          <p className="m-0 text-class-ink-soft">Nikt jeszcze się nie zapisał.</p>
        ) : (
          <Rows>
            {bookings.map((b) => (
              <div key={b.id} className="flex min-h-14 flex-wrap items-start gap-x-4 gap-y-1 py-3">
                <span className="w-[100px] shrink-0 font-semibold">
                  {timeIn(b.startsAt, OWNER_TIMEZONE)}–{timeIn(b.endsAt, OWNER_TIMEZONE)}
                </span>
                <span className="min-w-0 flex-1 leading-snug">
                  <span className="block font-semibold">{b.name}</span>
                  {b.email && (
                    <a href={`mailto:${b.email}`} className="block text-class-gloss">
                      {b.email}
                    </a>
                  )}
                  {b.goals && <span className="block text-class-gloss text-class-ink-soft">{b.goals}</span>}
                  {planItems
                    .filter((p) => p.bookingId === b.id)
                    .map((p, i) => (
                      <PlanClassLine key={`${p.planId}-${p.lessonSlug ?? i}`} item={p} clientId={b.clientId} />
                    ))}
                </span>
                {b.status !== 'confirmed' && (
                  <Status tone={b.status === 'pending' ? 'wait' : b.status === 'completed' ? 'ok' : 'neutral'}>
                    {BOOKING_STATUS_LABELS[b.status]}
                  </Status>
                )}
              </div>
            ))}
          </Rows>
        )}
      </SheetSection>

      {/* Windows already open. */}
      <SheetSection title="Otwarte godziny">
        {windows.length === 0 ? (
          <p className="m-0 text-class-ink-soft">Ten dzień jest zamknięty.</p>
        ) : (
          <Rows>
            {windows.map((w) => (
              <div key={w.id} className="flex min-h-14 items-center gap-4">
                <span className="min-w-0 flex-1">
                  <span className="font-semibold">
                    {hhmm(w.startTime)}–{hhmm(w.endTime)}
                  </span>
                  <span className="ml-3 text-class-gloss text-class-ink-soft">
                    lekcje po {SLOT_LENGTH_LABELS[w.slotMinutes as SlotLength] ?? `${w.slotMinutes} min`}
                  </span>
                </span>
                <button type="button" onClick={() => onDelete(w.id)} disabled={busy} className={DANGER_LINK}>
                  Usuń
                </button>
              </div>
            ))}
          </Rows>
        )}
        {windows.length > 0 && bookings.length > 0 && (
          <p className="m-0 text-class-gloss text-class-ink-soft">Usunięcie godzin nie odwołuje istniejących zapisów.</p>
        )}
      </SheetSection>

      {/* Open more time. */}
      <SheetSection
        title="Otwórz godziny"
        action={
          <span className="flex flex-wrap gap-x-4">
            {PRESETS.map((p) => (
              <button
                key={p.label}
                type="button"
                className="cl-link text-class-gloss"
                onClick={() => {
                  setStart(p.start)
                  setEnd(p.end)
                }}
              >
                {p.label}
              </button>
            ))}
          </span>
        }
      >
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <FieldLabel htmlFor="day-from">Od</FieldLabel>
            <input id="day-from" type="time" step={900} value={start} onChange={(e) => setStart(e.target.value)} className="cl-input w-32" />
          </div>
          <div>
            <FieldLabel htmlFor="day-to">Do</FieldLabel>
            <input id="day-to" type="time" step={900} value={end} onChange={(e) => setEnd(e.target.value)} className="cl-input w-32" />
          </div>
        </div>

        <div>
          <p className="cl-label m-0 mb-1.5">Długość lekcji</p>
          <Choice
            label="Długość lekcji"
            value={slot}
            onChange={setSlot}
            items={SLOT_LENGTHS.map((len) => ({ value: len, label: SLOT_LENGTH_LABELS[len] }))}
          />
        </div>

        {!valid && <ErrorLine>Godzina „do” musi być późniejsza niż „od”.</ErrorLine>}
        {error && <ErrorLine>{error}</ErrorLine>}

        <div className="pt-2">
          <Button disabled={!valid || busy} onClick={() => onAdd(start, end, slot)}>
            {busy ? 'Zapisuję…' : 'Otwórz te godziny'}
          </Button>
        </div>
      </SheetSection>
    </Sheet>
  )
}
